'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import { webPagesClient } from '@terra/shared-rest-clients-web-pages';
import {
  FEATURE_RELATED_REQUESTS_DEFAULT_LIMIT,
  type FeatureRelatedRequestDto,
} from '@terra/shared-contracts-web-pages';
import { FEATURE_RELATED_REQUESTS_QUERY_KEY } from './applicationAssetsQueryKeys';

export { FEATURE_RELATED_REQUESTS_QUERY_KEY } from './applicationAssetsQueryKeys';

type UseFeatureRelatedRequestsParams = {
  webpageId: string;
  featureId: string;
  enabled: boolean;
};

type FeatureRelatedRequestsPage = {
  relatedRequests: FeatureRelatedRequestDto[];
  totalCount: number;
  skip: number;
};

const fetchFeatureRelatedRequests = async ({
  webpageId,
  featureId,
  skip,
}: {
  webpageId: string;
  featureId: string;
  skip: number;
}): Promise<FeatureRelatedRequestsPage> => {
  const response = await webPagesClient.getFeatureRelatedRequests({
    params: { id: webpageId, featureId },
    query: { skip, limit: FEATURE_RELATED_REQUESTS_DEFAULT_LIMIT },
  });

  if (response.status === 200) {
    return {
      relatedRequests: response.body.relatedRequests,
      totalCount: response.body.totalCount,
      skip,
    };
  }

  throw new Error('Failed to load feature related requests');
};

export const useFeatureRelatedRequests = ({
  webpageId,
  featureId,
  enabled,
}: UseFeatureRelatedRequestsParams) => {
  const query = useInfiniteQuery({
    queryKey: [
      FEATURE_RELATED_REQUESTS_QUERY_KEY,
      webpageId,
      featureId,
      FEATURE_RELATED_REQUESTS_DEFAULT_LIMIT,
    ],
    queryFn: ({ pageParam }) =>
      fetchFeatureRelatedRequests({ webpageId, featureId, skip: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextSkip = lastPage.skip + FEATURE_RELATED_REQUESTS_DEFAULT_LIMIT;

      return nextSkip < lastPage.totalCount ? nextSkip : undefined;
    },
    enabled: enabled && webpageId.length > 0 && featureId.length > 0,
    staleTime: 30_000,
  });

  return {
    relatedRequests:
      query.data?.pages.flatMap((page) => page.relatedRequests) ?? [],
    totalCount: query.data?.pages[0]?.totalCount ?? 0,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    error: query.error,
  };
};
